import { Capacitor } from "@capacitor/core"
import webHttp from "./webHttp"
import { TokenVault } from "./TokenVault"
import { CheckInScheduleService } from "./CheckInScheduleService"

function buildClient(member, schedule = {}) {
  const nextAt = schedule.next_check_in_at ? new Date(schedule.next_check_in_at) : null
  const today = new Date()
  const overdue = nextAt !== null && nextAt < today
  const daysOverdue = overdue ? Math.floor((today - nextAt) / 86400000) : 0

  return {
    participantId: member.prefix_id,
    // id aliases participantId so existing templates keyed on client.id keep working
    id: member.prefix_id,
    numericId: member.id,
    firstName: member.first_name,
    lastName: member.last_name,
    fullName: `${member.first_name} ${member.last_name}`,
    email: member.email,
    checkInDayOfWeek: schedule.check_in_day_of_week ?? null,
    checkInFrequency: schedule.check_in_frequency ?? null,
    lastCheckInAt: schedule.last_check_in_at ?? null,
    nextCheckInAt: schedule.next_check_in_at ?? null,
    overdueReminderSentAt: schedule.overdue_reminder_sent_at ?? null,
    checkInOverdue: overdue,
    checkInDaysOverdue: daysOverdue,
  }
}

export const RosterService = {
  async fetchClients({ accountPrefixId, trainerId, archived } = {}) {
    const config = {}
    if (Capacitor.isNativePlatform()) {
      const token = await TokenVault.get()
      if (token) {
        config.headers = { Authorization: `Bearer ${token}` }
      }
    }
    const url = `/api/v1/accounts/${accountPrefixId}/trainers/${trainerId}/clients`
    if (archived) {
      config.params = { archived: true }
    }
    const response = await webHttp.get(url, config)
    return response.data
  },

  // Roster identity (web/) merged with the check-in schedule projection
  // (api/, participant-keyed) into the UI client shape (ADR 0001 §4).
  async fetchMergedClients({ accountPrefixId, trainerId, archived } = {}) {
    const roster = await this.fetchClients({ accountPrefixId, trainerId, archived })
    const prefixIds = roster.map((m) => m.prefix_id)
    let scheduleMap = {}
    if (prefixIds.length > 0) {
      const schedules = await CheckInScheduleService.fetchFor(prefixIds)
      scheduleMap = Object.fromEntries(schedules.map((s) => [s.client_participant_id, s]))
    }
    return roster.map((member) => buildClient(member, scheduleMap[member.prefix_id]))
  },

  async _buildConfig() {
    const config = {}
    if (Capacitor.isNativePlatform()) {
      const token = await TokenVault.get()
      if (token) {
        config.headers = { Authorization: `Bearer ${token}` }
      }
    }
    return config
  },

  async inviteClient({ accountPrefixId, trainerId, email, firstName, lastName }) {
    const config = await this._buildConfig()
    const url = `/api/v1/accounts/${accountPrefixId}/trainers/${trainerId}/clients/invitations`
    const response = await webHttp.post(url, { email, first_name: firstName, last_name: lastName }, config)
    return response.data
  },

  async archiveClient({ accountPrefixId, trainerId, clientNumericId }) {
    const config = await this._buildConfig()
    const url = `/api/v1/accounts/${accountPrefixId}/trainers/${trainerId}/clients/${clientNumericId}/archive`
    const response = await webHttp.patch(url, {}, config)
    return response.data
  },

  async unarchiveClient({ accountPrefixId, trainerId, clientNumericId }) {
    const config = await this._buildConfig()
    const url = `/api/v1/accounts/${accountPrefixId}/trainers/${trainerId}/clients/${clientNumericId}/unarchive`
    const response = await webHttp.patch(url, {}, config)
    return response.data
  },

  async removeClient({ accountPrefixId, trainerId, clientNumericId }) {
    const config = await this._buildConfig()
    const url = `/api/v1/accounts/${accountPrefixId}/trainers/${trainerId}/clients/${clientNumericId}`
    await webHttp.delete(url, config)
  },
}

export default RosterService
